/**
 * embed-health-beacon — the framed embed's OWN failure reporter (ADR-104 Layer 1).
 *
 * WHY THIS EXISTS, AND WHY NOTHING ELSE COULD DO ITS JOB
 * -----------------------------------------------------
 * Three ways the public sign-up surface breaks were, until this file, completely
 * unobservable. Not "hard to notice" — structurally invisible to every layer we
 * had:
 *
 *   1. **The gate read fails** (`gate-unreadable`). `SowEmbedPage.readGate`
 *      fails CLOSED on any error, which is correct — enforcement is server-side
 *      and must never be bypassed by a client hiccup. But failing closed means
 *      showing the BETA-INTEREST face. A visitor who should have been offered a
 *      paid sign-up gets a waiting list instead, and NOTHING reports it: no 5xx
 *      (the fetch failed client-side, or the Worker answered non-200 and we
 *      swallowed it), no server log (the sign-up was never attempted), and the
 *      synthetic probe is GREEN because a form did render. Revenue is lost
 *      silently. This is the first of ADR-104 §1's two probe-green blind spots.
 *
 *   2. **Turnstile never issues a token** (`turnstile-error`). The form renders
 *      completely; the submit button just never enables. The probe asserts a
 *      `<form>` exists inside the frame, so it passes. The visitor sees a working
 *      form they cannot submit. The second blind spot.
 *
 *   3. **The submit itself fails** (`submit-error`). A non-2xx or a network
 *      error on `POST /signup/applications` or `POST /signup/beta-interest`. The
 *      Lambda's own errors are now alarmed server-side (ADR-104 Layer 2), but
 *      that cannot see a request that never ARRIVED — DNS, TLS, a CORS
 *      regression, an edge 403. Only the caller can report those.
 *
 * The common thread: in all three the iframe came up perfectly, so only code
 * INSIDE the iframe can witness them. Hence `source: 'embed'` — distinct from
 * `shell` (the www modal, outside the frame, which can only see load failures)
 * and from `synthetic` (the probe).
 *
 * ── CONTRACT ───────────────────────────────────────────────────────────────
 * • **PII-FREE, ALWAYS.** The body is `{embed, phase, uaBucket, source}` and
 *   nothing else. No email, no business name, no promo code, no session token,
 *   no URL, no error message. The Worker re-sanitises every field to an opaque
 *   slug, but that is ITS defence, not a licence to be careless here: this runs
 *   on the unauthenticated public sign-up form, which is the single most
 *   sensitive input surface on the platform. If a future edit wants to attach
 *   "just the status code", note that a status code is fine and an error STRING
 *   is not — backend error text has leaked identifiers before.
 * • **BEST-EFFORT, NEVER BLOCKING.** Every call is fire-and-forget with its own
 *   catch. A beacon must never delay, fail, or alter what the visitor sees — the
 *   whole point is that these are moments the visitor is ALREADY having a bad
 *   time. `keepalive` lets it survive the page being torn down.
 * • **NOT AWAITED AT CALL SITES.** Deliberate. Awaiting would put the Worker's
 *   latency inside the visitor's error path.
 *
 * ── VOLUME: HUMAN-PACED PATHS ARE UNCAPPED, MACHINE-PACED ONES ARE NOT ──────
 * The counts ARE the severity signal, so blanket de-duplication would be wrong:
 * it would turn "this visitor tried four times and failed four times" into "1".
 * `submit-error` is therefore uncapped — it fires only when a person presses a
 * button, so its volume is bounded by human patience.
 *
 * The other two are NOT human-paced and are bounded at their call sites:
 *   • `gate-unreadable` sits on a 60-second revalidation tick, so it is reported
 *     ONCE PER FAILURE STREAK (see SowEmbedPage.applyUnreadable) — otherwise an
 *     outage would emit a KV write per open tab per minute for its whole
 *     duration, and buy nothing, since the alert is critical at a count of one.
 *   • `turnstile-error` comes from Cloudflare's `error-callback`, which retries
 *     on some network-error classes without being asked — so it is capped per
 *     mount via `cappedBeacon` below.
 * The rule: cap where a MACHINE decides the rate, never where a person does.
 *
 * ── DELIBERATE ABSENCES ────────────────────────────────────────────────────
 * • **No success beacon.** The signal is failure-only by design (see the Worker's
 *   header note) — there is no success denominator, and adding one would put a
 *   write on the happy path of every sign-up for no diagnostic gain.
 * • **No retry.** A beacon that fails is lost. Retrying would spend the
 *   visitor's connection on telemetry at the exact moment their connection is
 *   the thing in doubt. The alert bands tolerate loss; they are thresholds over
 *   a 2h window, not exact accounting.
 */
import { ENV } from '../amplify-config';

/** Which embed the failure happened in — matches the Worker's `EmbedKind`. */
export type EmbedKindTag = 'sow' | 'signin';

/**
 * The phases this module may report. A CLOSED union on purpose: the Worker maps
 * phase → failure family (and therefore → which alert fires, with which copy and
 * which severity), so an ad-hoc phase string invented at a call site would land
 * in the Worker's `other` family and read as unclassified rather than raising the
 * alert its author intended. Adding a phase here means adding it to
 * `PHASE_FAMILY` in workers/sow-gate/src/embed-health.ts, in the same change.
 */
export type EmbedFailurePhase = 'submit-error' | 'turnstile-error' | 'gate-unreadable';

/**
 * Coarse UA bucket. Deliberately coarse — this is for spotting a
 * browser-specific frame/challenge failure, not for identifying anyone. The
 * Worker caps it at 24 slug chars regardless.
 *
 * Order matters: Edge and Chrome both contain "chrome" in their UA strings, and
 * Safari's UA contains "safari" alongside Chrome's, so the specific engines are
 * tested before the generic ones.
 */
export function uaBucket(userAgent: string = navigator.userAgent): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('chrome') || ua.includes('chromium')) return 'chrome';
  if (ua.includes('safari')) return 'safari';
  return 'other';
}

/**
 * Report one embed failure. Fire-and-forget: returns immediately, resolves when
 * the POST settles, and NEVER rejects.
 *
 * `void beaconEmbedFailure('sow', 'submit-error')` at the call site — the `void`
 * is what documents that the result is intentionally ignored (and keeps
 * no-floating-promises happy).
 */
export async function beaconEmbedFailure(
  embed: EmbedKindTag,
  phase: EmbedFailurePhase,
): Promise<void> {
  const url = ENV.EMBED_HEALTH_URL;
  if (!url) return;
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `keepalive`: these fire at moments the visitor may immediately close the
      // modal or reload — without it the request is cancelled on teardown and
      // the very failures most likely to make someone give up are the ones least
      // likely to be recorded.
      keepalive: true,
      body: JSON.stringify({
        embed,
        phase,
        uaBucket: uaBucket(),
        // `embed` (not `shell`): reported from INSIDE the frame, which is itself
        // evidence the frame rendered. See the Worker's EmbedFailureSource doc.
        source: 'embed',
      }),
    });
  } catch {
    // Swallowed on purpose. A telemetry failure must never surface to the
    // visitor or change control flow — and there is nowhere useful to report a
    // failure to report a failure. Not even a console.error: this runs on the
    // public marketing surface, and a red console line on a page that is
    // otherwise working invites a false bug report.
  }
}

/**
 * Wrap `beaconEmbedFailure` with a hard per-instance cap, for the call sites
 * whose rate a MACHINE decides rather than a person (see the volume note above).
 *
 * Create ONE per component mount and hold it in a ref — the closure counter is
 * what makes the cap per-mount rather than global. A global cap would be wrong:
 * it would silence the second visitor of the session because the first had
 * already spent the budget.
 *
 * `max` defaults to 3: enough that a single transient blip and its couple of
 * automatic retries still read as "more than one", which is where the Turnstile
 * band sits, without letting a retry loop write unbounded.
 */
export function cappedBeacon(max = 3): (embed: EmbedKindTag, phase: EmbedFailurePhase) => void {
  let sent = 0;
  return (embed, phase) => {
    if (sent >= max) return;
    sent++;
    void beaconEmbedFailure(embed, phase);
  };
}
